/** The closed set of event names. Deliberately small: the point is to start
 *  with the set that answers the four questions the spec asks and grow it from
 *  evidence, not to instrument everything and sort it out in PostHog.
 *
 *  ⚠️ A new name goes HERE first. `capture()` is typed against this union, so
 *  a stray string literal at a call site is a compile error rather than a
 *  silently misspelled event nobody notices for a month. */
export type AnalyticsEvent =
  /** Once per session per surface, with `surface` and `roles`. */
  | 'surface_opened'
  /** The coach practice questionnaire. */
  | 'survey_started'
  | 'survey_submitted'
  /** The referee scheduler. */
  | 'slot_claimed'
  | 'slot_confirmed'
  | 'slot_released'
  /** Stats: card intake and the statistician's review queue. */
  | 'card_submitted'
  | 'card_review_completed'
  /** The card wizard's bot check refused a submitter: which credential was
   *  missing or stale, never who they are. */
  | 'card_check_failed'
  /** Coach portal. */
  | 'game_card_opened'
  /** An unhandled exception IN A BROWSER: a message and a route, never a
   *  payload. Somebody was looking at a screen when this happened. */
  | 'client_error'
  /** A background Worker failure — a cron trigger or a queue job. Distinct
   *  from `client_error` because nobody was there: it has a job label, not a
   *  route, and it lands on `surface: 'worker'` with no person attached. */
  | 'server_error'

/** ⚠️ SCALARS ONLY, and `string` here means an IDENTIFIER or a LABEL — a season
 *  id, a division name, a route, a count, a duration. It is NOT a place to put
 *  free text a person typed, a player's name, or any page content. The spec's
 *  PII rules govern what may be assigned; this type only stops objects and
 *  arrays from being smuggled through wholesale. */
export type EventProps = Record<string, string | number | boolean | null | undefined>

/** ⚠️ ONE RULE, TWO RUNTIMES. A `client_error` handler is installed twice: by
 *  `client.ts`, as bundled TypeScript for the six SPA surfaces, and by
 *  `snippet.ts`, as an ES5 string for the server-rendered pages that ship no
 *  bundle and so cannot import it. The two cannot share code — pulling in
 *  posthog-js is precisely what the snippet exists to avoid — so they share
 *  these instead. A cap or a label defined in only one of them is two rules
 *  wearing one name, which is how the platform's copied display rules have
 *  drifted before.
 *
 *  The cap is a VOLUME limit, not a redaction: see `installErrorHandler`'s own
 *  note on what actually keeps player data out of this event. */
export const CLIENT_ERROR_MESSAGE_CAP = 200

/** The two ways a `client_error` reaches us, as the `kind` property's values. */
export const CLIENT_ERROR_KINDS = {
  /** A `window.onerror` ErrorEvent. */
  thrown: 'error',
  /** A promise rejection nothing caught. */
  rejection: 'unhandledrejection',
} as const
